import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function BookSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [booksList, setBooksList] = useState([]);
  const [filteredBooksList, setFilteredBooksList] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      title: "",
      isbn: "",
      publishedYear: "",
      genre: "",
      copiesAvailable: "",
      authorID: "",
      libraryId: "",
      fine: "",
      image: "",
      imagePath: "",
      base64String: "",
      stockQty: 0,
      barcode: "",
      createdBy: localStorage.getItem("userId"),
    },
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [libraryId, setLibraryId] = useState("");
  const [centersData, setCentersData] = useState([]);
  const [librariesData, setLibrariesData] = useState([]);
  const [authorsList, setAuthorsList] = useState([]);

  const [bookImageName, setBookImageName] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCenters();
  }, [organizationId]);

  const getLibrariesListByCenterId = async (centerId) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `LMS/GetLibraryByCenterIdAsync?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLibrariesData(response?.libraryService);
    } catch (error) {
      console.error("Error fetching libraries:", error);
    }
  };

  useEffect(() => {
    if (centerId) {
      getLibrariesListByCenterId(centerId);
    }
  }, [centerId]);

  useEffect(() => {
    const getAuthorsList = async () => {
      if (!token) {
        console.error("Unauthorized.");
        return;
      }

      try {
        const response = await get(`LMS/GetAuthorAsync`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setAuthorsList(response?.authorsService);
      } catch (error) {
        console.error("Error fetching authors:", error);
      }
    };

    getAuthorsList();
  }, [organizationId]);

  const getBooksListByLibraryId = async (libraryId) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `LMS/GetBooksBylibraryIdAsync?libraryId=${libraryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setBooksList(response?.bookService || []);
      setFilteredBooksList(response?.bookService || []); // Initialize filtered list
    } catch (error) {
      console.error("Error fetching books:", error);
    }
  };

  useEffect(() => {
    if (libraryId) {
      getBooksListByLibraryId(libraryId);
    }
  }, [triggerRerender, libraryId]);

  const handleEdit = async (bookId) => {
    try {
      const response = await get(`LMS/GetBooksByIdAsync?bookId=${bookId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { bookService: singleBookData } = response;

      const imageUrl = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${singleBookData.imagePath}`;

      setPreviewUrl(imageUrl);
      setBookImageName(singleBookData.centerImage);

      reset({
        ...singleBookData,
        id: singleBookData.id,
        updatedBy: localStorage.getItem("userId"),
        centerId: getValues("centerId"),
        libraryId: getValues("libraryId"),
      });
    } catch (error) {
      console.error("Error editing book:", error);
    }
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "title",
        header: "Title",
      },
      {
        accessorKey: "copiesAvailable",
        header: "No. of Copies",
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const filteredData = filteredBooksList.map((row, index) => ({
      "Sr No": index + 1,
      Title: row.title,
      "No. of Copies": row.copiesAvailable,
    }));

    console.log(filteredData);
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Books");
    XLSX.writeFile(workbook, "Books.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    
    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Books List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = filteredBooksList.map((row, index) => {
        return [
          index + 1,
          row.title,
          row.copiesAvailable,
        ];
      });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("BooksList.pdf");
  };
  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const handleCenterDropdownChange = (event) => {
    const selectedLibraryId = event.target.value;
    setLibraryId(selectedLibraryId);
    getBooksListByLibraryId(selectedLibraryId);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setBookImageName(file.name);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setValue("base64String", base64String, { shouldValidate: true });
        setPreviewUrl(reader.result);
        trigger("image");
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      libraryId: libraryId,
      image: bookImageName,
      centerId: centerId,
    };

    try {
      const response = await post("LMS/AddUpdateBooksAsync", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        setPreviewUrl(null);
        reset({
          id: 0,
          title: "",
          isbn: "",
          publishedYear: "",
          genre: "",
          copiesAvailable: "",
          authorID: "",
          libraryId: "",
          fine: "",
          image: "",
          imagePath: "",
          base64String: "",
          stockQty: "",
          barcode: "",
          createdBy: localStorage.getItem("userId"),
          centerId: getValues("centerId"),
          libraryId: getValues("libraryId"),
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  useEffect(() => {
    const filtered = booksList.filter((book) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        book.title.toLowerCase().includes(searchLower) ||
        book.copiesAvailable.toString().includes(searchLower)
      );
    });
    setFilteredBooksList(filtered);
  }, [searchTerm, booksList]);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Books</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Center<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name={`centerId`}
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centersData.map((center) => ({
                          value: center.id,
                          label: center.name,
                        }))}
                        isDisabled={savedCenterId !== "0"}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue(`centerId`, null);
                            setValue("libraryId", null); // Clear library
                            setValue("libraryMemberId", null); // Clear member
                            // setValue("bookId", null); // Clear book
                            setCenterId(null);
                            setLibrariesData([]); // Reset libraries

                            setBooksList([]); // Reset books
                          } else {
                            setValue(
                              `centerId`,
                              selectedOption || { value: 0, label: "" }
                            );
                            setCenterId(selectedOption.value);
                            setValue("libraryId", null); // Clear library
                            setValue("libraryMemberId", null); // Clear member
                            // setValue("bookId", null); // Clear book
                            setLibrariesData([]); // Reset libraries

                            setBooksList([]); // Reset books
                            // trigger(["libraryId", "libraryMemberId", "bookId"]); // Validate cleared fields
                          }
                          trigger(`centerId`);
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.centerId && (
                    <small className="text-danger">
                      {errors.centerId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Library<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name={`libraryId`}
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={librariesData.map((library) => ({
                          value: library.id,
                          label: library.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue(`libraryId`, null);
                            setLibraryId("");
                            // setValue("bookId", null); // Clear book
                            setBooksList([]); // Reset books
                          } else {
                            setValue(
                              `libraryId`,
                              selectedOption || { value: 0, label: "" }
                            );
                            setLibraryId(selectedOption.value);
                            getBooksListByLibraryId(selectedOption.value); // Fetch books for the selected library
                            // setValue("bookId", null); // Clear book
                            setBooksList([]); // Reset books
                          }
                          trigger(`libraryId`);
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.libraryId && (
                    <small className="text-danger">
                      {errors.libraryId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Author<span className="text-danger fs-9">*</span>
                  </label>
                  <select
                    {...register(`authorID`, {
                      required: "This field is required.",
                    })}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    {authorsList.map((author) => (
                      <option key={author.id} value={author.id}>
                        {author.name}
                      </option>
                    ))}
                  </select>
                  {errors.authorID && (
                    <small className="text-danger">
                      {errors.authorID.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Title<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`title`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("title")}
                  />
                  {errors.title && (
                    <small className="text-danger">
                      {errors.title.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    ISBN No.<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`isbn`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("isbn")}
                  />
                  {errors.isbn && (
                    <small className="text-danger">{errors.isbn.message}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Genre<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`genre`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("genre")}
                  />
                  {errors.genre && (
                    <small className="text-danger">
                      {errors.genre.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Published Year<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("publishedYear", {
                      required: "This field is required.",
                      validate: (value) =>
                        /^\d{4}$/.test(value) ||
                        "Year must be a 4-digit number.",
                    })}
                    type="text"
                    placeholder="YYYY"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/[^0-9]/g, "")
                        .slice(0, 4);
                    }}
                  />

                  {errors.publishedYear && (
                    <small className="text-danger">
                      {errors.publishedYear.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    No. of Copies<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder=""
                    {...register(`copiesAvailable`, {
                      valueAsNumber: true,
                      required: "This field is required",
                      validate: (value) =>
                        value >= 0 || "Please enter correct value.",
                    })}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "+" ||
                        e.key === "."
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value < 0 ? 1 : e.target.value;
                    }}
                  />
                  {errors.copiesAvailable && (
                    <small className="text-danger">
                      {errors.copiesAvailable.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Fine per day<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    {...register(`fine`, {
                      valueAsNumber: true,
                      required: "This field is required",
                      validate: (value) =>
                        value >= 0 || "Please enter correct value.",
                    })}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "+" ||
                        e.key === "."
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value < 0 ? 1 : e.target.value;
                    }}
                  />
                  {errors.fine && (
                    <small className="text-danger">{errors.fine.message}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Barcode<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register(`barcode`, {
                      // valueAsNumber: true,
                      required: "This field is required",
                      // validate: (value) =>
                      //   value >= 0 || "Please enter correct value.",
                    })}
                  />
                  {errors.fine && (
                    <small className="text-danger">
                      {errors.barcode.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6"></div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Preview Image
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <div className="image-uploader-wrapper-2 d-flex flex-column align-items-center">
                    <div className="image-preview d-flex justify-content-center align-items-center flex-shrink-0 mb-2 mt-1">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Preview" />
                      ) : (
                        <div>
                          <span className="fas fa-image fs-3 text-light"></span>
                        </div>
                      )}
                    </div>
                    <input
                      {...register("image", {
                        required: !previewUrl
                          ? "This field is required."
                          : false, // Conditionally set required
                      })}
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>
                  {errors.image && (
                    <small className="text-danger">
                      {errors.image.message}
                    </small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={filteredBooksList}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                    columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BookSetup;
